import { useCallback, useEffect, useState } from "react";
import { Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { download } from "@/lib/download";
import { formatDate } from "@/lib/format";
import ImportWizard from "@/components/import/ImportWizard";
import { DemoDataCard } from "@/components/import/DemoDataCard";
import { useAuth } from "@/context/AuthContext";

const LABEL = { cariler: "Cariler", urunler: "Ürünler", fiyat: "Fiyat (Excel)", fiyat_kural: "Fiyat (Kural)", hesaplar: "Kasa/Banka açılış" };

export default function DataImportTab() {
  const [items, setItems] = useState(null);
  const [wiz, setWiz] = useState(null);
  const { user } = useAuth();
  const load = useCallback(() => api.get("/data-import/batches").then(({ data }) => setItems(data.items)), []);
  useEffect(() => { load(); }, [load]);
  const rollback = async (b) => {
    if (!window.confirm(`${b.id} aktarımı geri alınsın mı? Oluşturulan kayıtlar silinir, güncellenenler eski haline döner.`)) return;
    try { const { data } = await api.post(`/data-import/batches/${b.id}/rollback`); toast.success(`Geri alındı: ${data.deleted} silindi, ${data.restored} geri yüklendi`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="data-import-tab">
      <div className="panel p-4">
        <div className="font-semibold mb-2">Excel ile Veri Aktarımı</div>
        <div className="flex flex-wrap gap-2">
          {["cariler", "urunler", "fiyat", "hesaplar"].map((k) => <Button key={k} variant="outline" onClick={() => setWiz(k)} data-testid={`data-import-start-${k}`}>{LABEL[k]}</Button>)}
          {["Stok (açılış)", "Çek/Senet"].map((l) => <Button key={l} variant="outline" disabled title="Dosya geldiğinde etkinleşecek">{l} · yakında</Button>)}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Başlık satırı otomatik bulunur (birleşik başlık ve toplam satırları atlanır), BizimHesap başlıkları otomatik eşlenir. Önizleme → onay → aktarım; her aktarım geri alınabilir.</p>
      </div>
      <div className="panel overflow-x-auto">
        <table className="erp-table text-[12px]" data-testid="import-batches"><thead><tr><th>Aktarım No</th><th>Tür</th><th>Dosya</th><th>Tarih</th><th>Yapan</th><th>Sonuç</th><th>Durum</th><th></th></tr></thead>
          <tbody>{!items ? null : items.length === 0 ? <tr><td colSpan={8} className="text-slate-400">Henüz aktarım yok</td></tr> : items.map((b) => (
            <tr key={b.id} data-testid={`import-batch-${b.id}`}><td className="font-mono">{b.id}</td><td>{LABEL[b.importer]}</td><td className="max-w-[160px] truncate">{b.filename}</td>
              <td>{formatDate(b.at)}</td><td>{b.by}</td><td>{Object.entries(b.counts || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}</td>
              <td>{b.status === "geri_alindi" ? <span className="text-amber-700">Geri alındı</span> : b.status === "tamamlandi" ? "Tamamlandı" : <span className="text-rose-700" title={b.note}>Yarıda kaldı</span>}</td>
              <td className="whitespace-nowrap">{b.importer !== "fiyat_kural" && <Button size="sm" variant="ghost" className="h-7" onClick={() => download(`/data-import/batches/${b.id}/report.xlsx`, null, `${b.id}.xlsx`)} data-testid={`import-batch-report-${b.id}`}><Download className="h-3.5 w-3.5" /></Button>}
                {b.status !== "geri_alindi" && <Button size="sm" variant="ghost" className="h-7 text-rose-600" onClick={() => rollback(b)} data-testid={`import-batch-rollback-${b.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Bu aktarımı geri al</Button>}</td></tr>))}</tbody></table>
      </div>
      {user?.is_super && <DemoDataCard />}
      {wiz && <ImportWizard importer={wiz} title={`${LABEL[wiz]} aktarımı`} onClose={() => setWiz(null)} onDone={load} />}
    </div>
  );
}
